import type { DeviceLive, DeviceStatic } from '../../realtime/types';

/**
 * Map device layer driven by MapLibre feature-state (decision D7, docs/architecture/06-frontend.md §4).
 * The GeoJSON source is built ONCE; a live change calls setFeatureState for that device only, and only when
 * its visual category changes. setData is never called for realtime updates.
 */
export type VisualCategory = 'critical' | 'warning' | 'offline' | 'unknown' | 'online';

export function visualCategory(d: DeviceLive | undefined): VisualCategory {
  if (!d) return 'unknown';
  if (d.alarm === 'critical') return 'critical';
  if (d.alarm === 'warning') return 'warning';
  return d.presence ?? 'unknown';
}

/**
 * Map legend entries: alarm severities colour a marker only for accounts that see alarms (alarm.view); the others
 * (authority observer) get no severity, so the legend does not list it either.
 */
export function legendCategories(seesAlarms: boolean): VisualCategory[] {
  return seesAlarms
    ? ['online', 'offline', 'warning', 'critical', 'unknown']
    : ['online', 'offline', 'unknown'];
}

/**
 * Light state shown as a glow under the marker (the marker itself keeps connectivity/alarm): 0 = no glow, otherwise the
 * dimming level rounded up to 10 % steps (10…100). Only an online device with dimming > 0 glows — an offline or unknown
 * device never does, whatever its last reported dimming.
 */
export function lightLevel(d: DeviceLive | undefined): number {
  const dim = d?.metrics?.['light.dimmer_pct'];
  if (d?.presence !== 'online' || dim === undefined || !(dim > 0)) return 0;
  return Math.min(100, Math.ceil(dim / 10) * 10);
}

/** Minimal slice of the MapLibre API used here (lets tests use a fake map). */
export interface FeatureStateMap {
  setFeatureState(feature: { source: string; id: number }, state: Record<string, unknown>): void;
}

export const SOURCE_ID = 'devices';

/** feature-state `sel` (device card open) → `on`, otherwise `off` */
const sel = (on: number, off: number) => ['case', ['boolean', ['feature-state', 'sel'], false], on, off];
/** feature property `hub` (other devices are reached through it, e.g. a concentrator): drawn larger */
const hub = (on: unknown, off: unknown) => ['case', ['boolean', ['get', 'hub'], false], on, off];
/** feature-state `rel`: reached through the selected device */
const rel = ['boolean', ['feature-state', 'rel'], false];

/**
 * Paint of the device circles: colour by visual category, enlarged + outlined when selected — all from
 * feature-state. MapLibre allows only ONE zoom interpolation, at the top level; the selection case sits in the stops
 * (a nested zoom expression makes the whole layer invalid: no device is drawn).
 */
export function devicePaint(color: (cssVar: string) => string) {
  return {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      8,
      hub(sel(8, 5), sel(6, 2)),
      13,
      hub(sel(10, 7), sel(8, 4)),
      16,
      hub(sel(13, 10), sel(11, 7)),
    ],
    'circle-stroke-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      8,
      ['case', rel, 2, hub(2, sel(3, 0))],
      14,
      ['case', rel, 2, hub(3, sel(3, 1))],
    ],
    'circle-stroke-color': [
      'case',
      ['boolean', ['feature-state', 'sel'], false],
      color('--color-accent'),
      rel,
      color('--color-accent'),
      hub(color('--color-text'), color('--color-surface')),
    ],
    'circle-color': [
      'match',
      ['coalesce', ['feature-state', 'cat'], 'unknown'],
      'online',
      color('--color-online'),
      'offline',
      color('--color-offline'),
      'warning',
      color('--color-warning'),
      'critical',
      color('--color-critical'),
      color('--color-unknown'),
    ],
  };
}

/** glow level 0..1 from feature-state `glow` (0..100) */
const glow = ['/', ['number', ['feature-state', 'glow'], 0], 100];
const grow = (base: number, extra: number) => ['+', base, ['*', extra, glow]];

/**
 * Paint of the light glow drawn under each device marker. It marks the light of one device, not an area: the radius
 * follows the zoom and stays close to the marker (zoomed out it is barely larger than the marker); the dimming level
 * mostly drives the opacity and only slightly the size. Invisible at level 0. Driven by feature-state only.
 */
export function glowPaint(color: (cssVar: string) => string) {
  return {
    'circle-color': color('--color-light-glow'),
    'circle-blur': 0.75,
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      8,
      grow(4, 1),
      12,
      grow(6, 1.5),
      15,
      grow(10, 3),
      18,
      grow(15, 4),
    ],
    'circle-opacity': ['case', ['>', glow, 0], ['+', 0.2, ['*', 0.7, glow]], 0],
    'circle-stroke-width': 0,
  };
}

export interface DeviceFeatureCollection {
  type: 'FeatureCollection';
  features: {
    type: 'Feature';
    id: number;
    geometry: { type: 'Point'; coordinates: [number, number] };
    /** pid: the device's project ('' = none), for the map filter by project */
    properties: { did: string; pid: string; hub?: boolean };
  }[];
}

export class DevicesLayer {
  /** device id → stable numeric feature id (MapLibre feature-state requires numeric ids) */
  private readonly fid = new Map<string, number>();
  private readonly applied = new Map<string, VisualCategory>();
  private readonly appliedGlow = new Map<string, number>();
  private selected: string | null = null;
  private related: string[] = [];

  constructor(private readonly map: FeatureStateMap) {}

  buildSource(
    devices: readonly DeviceStatic[],
    hubs: ReadonlySet<string> = new Set(),
  ): DeviceFeatureCollection {
    const features: DeviceFeatureCollection['features'] = [];
    for (const d of devices) {
      if (d.lon === undefined || d.lat === undefined) continue;
      let id = this.fid.get(d.id);
      if (id === undefined) this.fid.set(d.id, (id = this.fid.size + 1));
      features.push({
        type: 'Feature',
        id,
        geometry: { type: 'Point', coordinates: [d.lon, d.lat] },
        properties: hubs.has(d.id)
          ? { did: d.id, pid: d.projectId ?? '', hub: true }
          : { did: d.id, pid: d.projectId ?? '' },
      });
    }
    return { type: 'FeatureCollection', features };
  }

  /** Applies the visual state (category, light glow) of the changed devices; returns how many features were touched. */
  applyChanges(changed: Iterable<string>, get: (id: string) => DeviceLive | undefined): number {
    let n = 0;
    for (const id of changed) {
      const fid = this.fid.get(id);
      if (fid === undefined) continue;
      const d = get(id);
      const state: { cat?: VisualCategory; glow?: number } = {};
      const cat = visualCategory(d);
      if (this.applied.get(id) !== cat) this.applied.set(id, (state.cat = cat));
      const level = lightLevel(d);
      if ((this.appliedGlow.get(id) ?? 0) !== level || !this.appliedGlow.has(id))
        this.appliedGlow.set(id, (state.glow = level));
      if (state.cat === undefined && state.glow === undefined) continue;
      this.map.setFeatureState({ source: SOURCE_ID, id: fid }, state);
      n++;
    }
    return n;
  }

  /** After a style swap (theme change) MapLibre drops feature-state: re-apply everything once. */
  reapplyAll(get: (id: string) => DeviceLive | undefined): number {
    this.applied.clear();
    this.appliedGlow.clear();
    if (this.selected !== null) this.select(this.selected, this.related);
    return this.applyChanges(this.fid.keys(), get);
  }

  /**
   * Highlights one device (the one whose card is open) and, when given, the devices reached through it; null clears
   * the highlight. Only the touched features change (feature-state), the source is never rebuilt.
   */
  select(id: string | null, related: readonly string[] = []): void {
    if (this.selected !== null) {
      const prev = this.fid.get(this.selected);
      if (prev !== undefined) this.map.setFeatureState({ source: SOURCE_ID, id: prev }, { sel: false });
    }
    for (const r of this.related) {
      const f = this.fid.get(r);
      if (f !== undefined) this.map.setFeatureState({ source: SOURCE_ID, id: f }, { rel: false });
    }
    this.selected = id;
    this.related = id === null ? [] : [...related];
    const fid = id === null ? undefined : this.fid.get(id);
    if (fid !== undefined) this.map.setFeatureState({ source: SOURCE_ID, id: fid }, { sel: true });
    for (const r of this.related) {
      const f = this.fid.get(r);
      if (f !== undefined) this.map.setFeatureState({ source: SOURCE_ID, id: f }, { rel: true });
    }
  }

  deviceIdOf(featureId: number): string | undefined {
    for (const [k, v] of this.fid) if (v === featureId) return k;
    return undefined;
  }
}
